import { useRef, useState } from 'react';
import { PlacesType } from 'react-tooltip';
import { shallowEqual, useSelector } from 'react-redux';
import { useTheme } from 'styled-components';
import Icon, { IconType } from 'components/Icon';
import { Tooltip } from 'components/Widget/Tooltip';
import { selectIsMobile } from 'redux/mode/reducers';
import { RootReducer } from 'redux/reducers';
import * as S from './styles';

export enum IconButtonState {
	REGULAR,
	SELECTED,
	DISABLED,
}

interface IconButtonProps {
	// if state is not provided, defaults to IconButtonState.REGULAR
	state?: IconButtonState;

	// optional icon to display
	icon?: IconType;
	iconWidth?: number;
	iconHeight?: number;

	// optional label text to display
	label?: string;

	// optional tooltip will appear on hover
	tooltip?: string;

	// optional placement of tooltip, can be
	// "top", "right", "bottom", or "left". defaults to "bottom-start"
	tooltipPlacement?: PlacesType;

	// this will be called when the button is clicked, if the state is not disabled
	onClick?: () => void;

	// optional custom colors for different button states
	colors?: {
		disabled?: string;
		hover?: string;
		active?: string;
		selected?: string;
		border?: string;
		text?: string;
	};

	// optional custom styling
	className?: string;
}

const IconButton = ({
	state,
	icon,
	iconWidth = 16,
	iconHeight = 16,
	label,
	tooltip,
	tooltipPlacement = 'bottom-start',
	onClick,
	colors,
	className,
}: IconButtonProps) => {
	const [tooltipId] = useState(String(Math.random()));
	const ref = useRef(null);
	const theme = useTheme();

	const { isMobile } = useSelector(
		(r: RootReducer) => ({
			isMobile: selectIsMobile(r.mode),
		}),
		shallowEqual
	);

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------
	const onButtonPress = (event: any) => {
		// make sure we're clicking the button! (and not the tooltips, which also trigger this event)
		if (event.target === ref.current) {
			if (onClick && state !== IconButtonState.DISABLED) {
				// If we have an onClick handler, don't let the event propagate
				event?.stopPropagation();
				event?.preventDefault();

				// Hide tooltip on click - this is especially important when the button triggers a modal
				// or other UI that takes over the screen, as the button won't receive mouse events to hide the tooltip
				if (tooltip) {
					const tooltipElement = document.querySelector(`[data-tooltip-id="${tooltipId}"]`);
					if (tooltipElement) {
						(tooltipElement as any).__reactTooltip?.hide();
					}
				}

				onClick();
			}
		}
	};

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------

	const renderBase = () => {
		return (
			<S.Base
				ref={ref}
				state={state || IconButtonState.REGULAR}
				colors={colors}
				onClick={onButtonPress}
				className={className}
				isMobile={isMobile}
			>
				{icon && (
					<S.Icon>
						<Icon
							type={icon}
							width={iconWidth}
							height={iconHeight}
							color={colors?.text || theme.gray[1000]}
						/>
					</S.Icon>
				)}
				{label && <S.Label>{label}</S.Label>}
			</S.Base>
		);
	};

	if (tooltip) {
		const placement: PlacesType = tooltipPlacement ?? 'bottom-start';
		return (
			<>
				<a data-tooltip-id={tooltipId} data-tooltip-content={tooltip}>
					{renderBase()}
				</a>
				<Tooltip id={tooltipId} placement={placement} />
			</>
		);
	}
	return renderBase();
};

export default IconButton;
